'use client';

import { useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import getPhotoUrl from '@alltrails/core/utils/getPhotoUrl';
import Image from '@alltrails/core/components/Image';
import ArrowSq from '@alltrails/denali/icons/ArrowSq';
import IconButton from '@alltrails/denali/components/IconButton';
import StarFilled from '@alltrails/denali/icons/StarFilled';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import { metersToFormattedDistance, minutesToFormattedTime, ratingToFormattedRating } from '@alltrails/shared/utils/dataConversions';
import InfoDividerDot from '@alltrails/shared/components/cards/InfoDividerDot';
import { getTrailDifficultyLabelFromNumber } from '../../utils/trailDifficultyHelpers';
import { useAppSelector } from '../../redux';
import styles from './styles/styles.module.scss';
import { TrailListItemData } from './types/types';

type TrailListItemProps = {
  index: number;
  trail: TrailListItemData;
  variant?: 'primary' | 'inverse';
  hideDesktopArrow?: boolean;
  setHovered: () => void;
  removeHovered: () => void;
};

const TrailListItem = ({ index, trail, variant = 'primary', hideDesktopArrow = false, setHovered, removeHovered }: TrailListItemProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const intl = useIntl();
  const displayMetric = useAppSelector(state => state.context.displayMetric);

  const labelNumber = index + 1;
  const { name, slug, avgRating, difficulty, length, durationMinutes = 0, photoId, photoHash, locationName } = trail;

  const arrowVariant = useMemo(() => {
    return variant === 'primary' ? 'elevated' : 'default';
  }, [variant]);

  const photoUrl = useMemo(() => {
    if (!photoId || !photoHash) {
      return null;
    }
    return getPhotoUrl({ isOptimized: true, photo: { id: photoId, photoHash }, size: '500x500' });
  }, [photoId, photoHash]);

  const localizedRating = useMemo(() => {
    if (!avgRating) {
      return null;
    }
    return ratingToFormattedRating(avgRating, languageRegionCode);
  }, [avgRating, languageRegionCode]);

  const difficultyText = getTrailDifficultyLabelFromNumber(intl, difficulty);

  return (
    <li className={styles.item} onMouseEnter={setHovered} onMouseLeave={removeHovered}>
      <a className={styles.link} href={wrapUrlSafe(`/trail/${slug}`, languageRegionCode)} target="_blank">
        <div className={styles.mobileNumberColumn}>{labelNumber}</div>
        {photoUrl && <Image src={photoUrl} className={styles.image} alt={name} width={248} height={140} />}
        <div className={styles.textContent}>
          <h3 className={styles.heading}>
            <span className={styles.desktopNumberPrefix}>
              <FormattedMessage
                defaultMessage="#{labelNumber} - "
                values={{
                  labelNumber
                }}
              />
            </span>
            {name}
          </h3>
          {locationName && <div className={styles.locationName}>{locationName}</div>}
          <div className={styles.stats}>
            {!!avgRating && <StarFilled color="currentColor" size="xs" className={styles.star} />}
            {!!avgRating && <span className={styles.ratingSpacing}>{localizedRating}</span>}
            {!!difficulty && (
              <div>
                {!!avgRating && <InfoDividerDot />}
                <span>{difficultyText}</span>
              </div>
            )}
            {!!length && (
              <div className={styles.distance}>
                {(!!difficulty || !!avgRating) && <InfoDividerDot />}
                <span>{metersToFormattedDistance(intl, languageRegionCode, length, displayMetric ? 'km' : 'mi', 1)}</span>
              </div>
            )}
            {!!durationMinutes && (
              <div className={styles.duration}>
                <InfoDividerDot className={styles.dividerDot} />
                {minutesToFormattedTime(intl, durationMinutes, true)}
              </div>
            )}
          </div>
        </div>
        {!hideDesktopArrow && (
          <IconButton
            icon={{ Component: ArrowSq, orientation: 'right' }}
            title={name}
            testId="trail-list-item-arrow-button"
            variant={arrowVariant}
            className={styles.arrowButton}
          />
        )}
      </a>
    </li>
  );
};

export default TrailListItem;
